import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, Copy, Download, FileCheck2, FileText, PenLine, RefreshCw, Send, ShieldCheck, XCircle } from "lucide-react";
import { ReplaceRequestButton, ReplacementNote, RejectRequestButton, RejectedNote } from "../../components/ReplaceRequestButton";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const api = axios.create({ baseURL: API, withCredentials: true });

const input = "w-full bg-white/[0.04] border border-white/15 text-slate-100 placeholder:text-slate-500 px-3 py-2 text-xs focus:outline-none focus:border-nx-goldlight/70";
const smallBtn = "text-[11px] tracking-[0.1em] uppercase border border-white/20 hover:border-nx-gold/60 text-white px-3 py-2 transition-colors disabled:opacity-50";
const goldBtn = "text-[11px] tracking-[0.1em] uppercase bg-nx-gold hover:bg-nx-goldlight text-nx-navy font-semibold px-3 py-2 transition-colors disabled:opacity-50";

const statusClass = (s) =>
  s === "verified" || s === "signed" || s === "approved"
    ? "border-emerald-500/50 text-emerald-300 bg-emerald-500/10"
    : s === "rejected"
      ? "border-red-500/50 text-red-300 bg-red-500/10"
      : "border-amber-500/40 text-amber-300 bg-amber-500/10";

const fmt = (d) => (d ? new Date(d).toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");
const pretty = (v) => (v === true ? "Yes" : v === false ? "No" : v || "—");

const Detail = ({ label, value }) => (
  <div className="border-b border-white/5 py-2">
    <p className="text-[10px] tracking-[0.16em] uppercase text-slate-500">{label}</p>
    <p className="text-sm text-slate-200 mt-1 whitespace-pre-wrap">{pretty(value)}</p>
  </div>
);

const ReviewControls = ({ id, value, onChange, onSave, busy, testPrefix }) => (
  <div className="grid sm:grid-cols-[130px_1fr_auto] gap-2 mt-3">
    <select className={input} value={value.status} onChange={(e) => onChange(id, "status", e.target.value)} data-testid={`${testPrefix}-status`}>
      <option value="pending">Pending</option>
      <option value="verified">Verified</option>
      <option value="rejected">Rejected</option>
    </select>
    <input className={input} placeholder="HR comment / correction note" value={value.comment} onChange={(e) => onChange(id, "comment", e.target.value)} data-testid={`${testPrefix}-comment`} />
    <button type="button" onClick={() => onSave(id)} disabled={busy} className={smallBtn} data-testid={`${testPrefix}-save`}>Save</button>
  </div>
);

export const AdminCandidateOnboarding = ({ application, user, onChanged, onAuthFail }) => {
  const [data, setData] = useState(null);
  const [missing, setMissing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [inviteLink, setInviteLink] = useState("");
  const [reviews, setReviews] = useState({});
  const [esignType, setEsignType] = useState("nda");
  const [esignTitle, setEsignTitle] = useState("");
  const [esignFile, setEsignFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const canAccess = user?.role === "admin" || user?.role === "hr" || user?.onboarding_manager === true;

  const load = useCallback(async () => {
    if (!canAccess) return;
    setLoading(true);
    try {
      const { data: payload } = await api.get(`/career-onboarding/admin/${application.id}`);
      setData(payload);
      setMissing(false);
      setReviews({});
    } catch (err) {
      if (err?.response?.status === 404) {
        setData(null);
        setMissing(true);
      } else if (err?.response?.status === 401) onAuthFail?.();
      else if (err?.response?.status !== 403) toast.error(err?.response?.data?.detail || "Could not load onboarding");
      if (err?.response?.status === 403) setMissing(true);
    } finally {
      setLoading(false);
    }
  }, [application.id, canAccess, onAuthFail]);

  useEffect(() => { load(); }, [load]);

  const sendInvite = async (sendEmail = true) => {
    setBusy(true);
    try {
      const { data: payload } = await api.post(`/career-onboarding/admin/${application.id}/invite`, { send_email: sendEmail });
      setInviteLink(payload.onboarding_url || "");
      toast.success(sendEmail ? (payload.email_status === "sent" ? "Onboarding link emailed" : "Onboarding link created") : "Fresh copy link generated");
      await load();
      onChanged?.({ ...application, onboarding_status: "invited", onboarding_id: payload.onboarding_id });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send onboarding link");
    } finally {
      setBusy(false);
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(inviteLink);
      toast.success("Link copied");
    } catch {
      toast.error("Could not copy link");
    }
  };

  const cancelOnboarding = async () => {
    const reason = window.prompt("Cancel this candidate's onboarding? The link will stop working, any entered details are discarded, and the candidate is emailed.\n\nOptionally add a reason (shown to the candidate). Click OK to proceed, Cancel to abort.", "");
    if (reason === null) return;
    setBusy(true);
    try {
      const { data: payload } = await api.post(`/career-onboarding/admin/${application.id}/cancel`, { notify: true, reason: reason.trim() });
      setData(null);
      setMissing(true);
      setInviteLink("");
      onChanged?.({ ...application, onboarding_status: "" });
      toast.success(payload.email_status === "sent" ? "Onboarding cancelled — candidate notified by email" : "Onboarding cancelled");
    } catch (err) {
      if (err?.response?.status === 401) onAuthFail?.();
      else toast.error(err?.response?.data?.detail || "Could not cancel onboarding");
    } finally {
      setBusy(false);
    }
  };

  const reviewValue = (id, status = "pending", comment = "") => reviews[id] || { status, comment };
  const setReview = (id, field, value) => setReviews((p) => ({ ...p, [id]: { ...reviewValue(id), [field]: value } }));

  const saveSection = async (section) => {
    const value = reviewValue(section, data.sections?.[section]?.status, data.sections?.[section]?.comment);
    setBusy(true);
    try {
      const { data: payload } = await api.patch(`/career-onboarding/admin/${application.id}/sections/${section}`, value);
      setData(payload);
      toast.success("Section review saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save section review");
    } finally {
      setBusy(false);
    }
  };

  const requestReplacement = async (docId, reason) => {
    try {
      const { data: payload } = await api.post(`/career-onboarding/admin/${application.id}/documents/${docId}/request-replacement`, { reason });
      setData(payload);
      toast.success("Replacement request emailed to the candidate");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send the replacement request");
      throw err;
    }
  };

  const rejectDocument = async (docId, reason) => {
    try {
      const { data: payload } = await api.post(`/career-onboarding/admin/${application.id}/documents/${docId}/reject`, { reason });
      setData(payload);
      toast.success("Document rejected — candidate notified by email");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not reject the document");
      throw err;
    }
  };

  const saveDocument = async (docId) => {
    const doc = data.documents.find((d) => d.id === docId);
    const value = reviewValue(`doc-${docId}`, doc?.status, doc?.comment);
    setBusy(true);
    try {
      const { data: payload } = await api.patch(`/career-onboarding/admin/${application.id}/documents/${docId}`, value);
      setData(payload);
      toast.success("Document review saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save document review");
    } finally {
      setBusy(false);
    }
  };

  const download = async (path, filename) => {
    try {
      const res = await api.get(path, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename || "document";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not download document");
    }
  };

  const sendSignatureRequest = async (e) => {
    e.preventDefault();
    const fd = new FormData();
    fd.append("document_type", esignType);
    fd.append("title", esignTitle);
    if (esignFile) fd.append("custom_document", esignFile);
    setBusy(true);
    try {
      const { data: payload } = await api.post(`/career-onboarding/admin/${application.id}/esign`, fd);
      setData(payload);
      setEsignTitle("");
      setEsignFile(null);
      toast.success("E-signature request sent");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create e-signature request");
    } finally {
      setBusy(false);
    }
  };

  const approve = async () => {
    setBusy(true);
    try {
      const { data: payload } = await api.post(`/career-onboarding/admin/${application.id}/approve`, { sync_erp: true });
      setData(payload);
      onChanged?.({ ...application, onboarding_status: "approved" });
      toast.success("Candidate approved and synced to ERPNext");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Approval failed");
    } finally {
      setBusy(false);
    }
  };

  const toggleDlIdentity = async () => {
    setBusy(true);
    try {
      await api.patch(`/career-onboarding/admin/${application.id}/dl-identity`, { allow: !data.allow_dl_identity });
      toast.success(data.allow_dl_identity ? "Aadhaar + PAN now mandatory for this candidate" : "Driving licence allowed as ID for this candidate");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update the identity rule");
    } finally {
      setBusy(false);
    }
  };

  const resendWelcome = async () => {
    setBusy(true);
    try {
      await api.post(`/career-onboarding/admin/${application.id}/resend-welcome`);
      toast.success("Welcome pack re-sent to the candidate");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not resend welcome pack");
    } finally {
      setBusy(false);
    }
  };

  if (!canAccess) {
    return <p className="text-xs text-slate-500 mt-6" data-testid="onboarding-access-restricted">Candidate onboarding details are restricted to Admin and HR.</p>;
  }

  if (loading) return <p className="text-xs text-slate-500 mt-6">Loading onboarding…</p>;

  if (missing || !data) {
    return (
      <div className="mt-6 border border-white/10 bg-white/[0.02] p-5" data-testid="onboarding-empty">
        <p className="text-sm text-slate-300">Onboarding has not started for this candidate.</p>
        {application.status === "shortlisted" ? (
          <button onClick={() => sendInvite(true)} disabled={busy} className={`${goldBtn} mt-4`} data-testid="send-onboarding-link"><Send size={12} className="inline mr-1" /> {busy ? "Sending…" : "Send Onboarding Link"}</button>
        ) : (
          <p className="text-xs text-amber-300 mt-3">Shortlist this candidate first, then send the onboarding link.</p>
        )}
      </div>
    );
  }

  return (
    <div className="mt-7 border-t border-white/10 pt-6" data-testid="admin-candidate-onboarding">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div>
          <p className="text-[10px] tracking-[0.22em] uppercase text-nx-goldlight">Candidate Onboarding</p>
          <h4 className="text-white text-lg mt-1">{data.reference_number} · {data.status}</h4>
        </div>
        <div className="flex items-center gap-2">
          <span className={`border px-2.5 py-1 text-[10px] uppercase ${statusClass(data.status)}`} data-testid="onboarding-admin-status">{data.status}</span>
          {!["submitted", "approved"].includes(data.status) && <button onClick={() => sendInvite(false)} disabled={busy} className={smallBtn} data-testid="regenerate-onboarding-link"><Send size={12} className="inline mr-1" /> Fresh Link</button>}
          {data.status !== "approved" && <button onClick={cancelOnboarding} disabled={busy} className="border border-red-500/50 text-red-400 hover:bg-red-500/10 text-[11px] tracking-[0.08em] uppercase px-2.5 py-1.5 transition-colors" data-testid="cancel-onboarding"><XCircle size={12} className="inline mr-1" /> Cancel Onboarding</button>}
          <button onClick={load} className={smallBtn} data-testid="onboarding-admin-refresh"><RefreshCw size={12} className="inline" /></button>
        </div>
      </div>

      <div className="grid md:grid-cols-4 gap-3 mb-5">
        <div className="border border-white/10 p-3"><p className="text-[10px] uppercase text-slate-500">Progress</p><p className="text-white mt-1" data-testid="onboarding-progress-count">{data.progress.completed}/{data.progress.total}</p></div>
        <div className="border border-white/10 p-3"><p className="text-[10px] uppercase text-slate-500">Submitted</p><p className="text-white mt-1">{fmt(data.submitted_at)}</p></div>
        <div className="border border-white/10 p-3"><p className="text-[10px] uppercase text-slate-500">Expires</p><p className="text-white mt-1">{fmt(data.expires_at)}</p></div>
        <div className="border border-white/10 p-3"><p className="text-[10px] uppercase text-slate-500">ERP Sync</p><p className="text-white mt-1" data-testid="onboarding-erp-status">{data.erp_sync?.status || "not started"}</p></div>
      </div>

      {canAccess && (
        <label className="flex items-center gap-3 text-xs text-slate-400 mb-5 cursor-pointer" data-testid="dl-identity-toggle-row">
          <input type="checkbox" className="accent-[#d4af5e]" checked={!!data.allow_dl_identity} disabled={busy} onChange={toggleDlIdentity} data-testid="dl-identity-toggle" />
          Allow driving licence instead of Aadhaar + PAN for this candidate (identity requirement is relaxed only when enabled)
        </label>
      )}

      {inviteLink && (
        <div className="mb-5 border border-nx-gold/30 bg-nx-gold/[0.06] p-3 flex flex-wrap items-center gap-2" data-testid="onboarding-link-box">
          <input readOnly className={`${input} flex-1 font-mono`} value={inviteLink} />
          <button onClick={copyLink} className={smallBtn} data-testid="copy-onboarding-link"><Copy size={12} className="inline mr-1" /> Copy</button>
        </div>
      )}

      <div className="grid gap-4">
        {Object.entries(data.sections || {}).map(([key, sec]) => {
          const sectionData = data.data?.[key] || {};
          return (
            <div key={key} className="border border-white/10 bg-white/[0.02] p-4" data-testid={`hr-section-${key}`}>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div><p className="text-white text-sm font-medium">{sec.label || key}</p>{sec.reviewed_by && <p className="text-[11px] text-slate-500 mt-1">Reviewed by {sec.reviewed_by} · {fmt(sec.reviewed_at)}</p>}</div>
                <span className={`border px-2.5 py-1 text-[10px] uppercase ${statusClass(sec.status)}`} data-testid={`hr-section-status-${key}`}>{sec.status}</span>
              </div>
              {key !== "documents" && key !== "signature" && (
                <div className="grid md:grid-cols-2 gap-x-5 mt-3">
                  {Object.entries(sectionData).map(([field, value]) => <Detail key={field} label={field.replaceAll("_", " ")} value={value} />)}
                </div>
              )}
              {key === "documents" && <p className="text-xs text-slate-500 mt-3">Review uploaded files below.</p>}
              {key === "signature" && <p className="text-xs text-slate-500 mt-3">Review e-signature requests below.</p>}
              <ReviewControls id={key} value={reviewValue(key, sec.status, sec.comment)} onChange={setReview} onSave={saveSection} busy={busy} testPrefix={`hr-review-${key}`} />
            </div>
          );
        })}
      </div>

      <div className="mt-6 border border-white/10 p-4" data-testid="hr-document-review">
        <h5 className="text-white text-sm font-medium mb-4"><FileCheck2 size={14} className="inline mr-2 text-nx-goldlight" />Uploaded Documents</h5>
        {(data.documents || []).length === 0 ? <p className="text-xs text-slate-500">No onboarding documents uploaded yet.</p> : (
          <div className="grid gap-4">
            {data.documents.map((doc) => (
              <div key={doc.id} className="border border-white/10 p-3" data-testid={`hr-document-${doc.id}`}>
                <div className="flex flex-wrap items-center gap-3">
                  <FileText size={14} className="text-nx-goldlight" />
                  <div className="min-w-48"><p className="text-sm text-white">{doc.label}</p><p className="text-[11px] text-slate-500">{doc.filename} · {Math.round((doc.size || 0) / 1024)} KB</p></div>
                  <span className={`border px-2 py-1 text-[10px] uppercase ${statusClass(doc.status)}`}>{doc.status}</span>
                  <ReplacementNote at={doc.replacement_requested_at} testId={`hr-document-replace-note-${doc.id}`} />
                  <RejectedNote at={doc.rejected_at} reason={doc.rejected_reason} testId={`hr-document-reject-note-${doc.id}`} />
                  <span className="ml-auto flex items-center gap-2">
                    {doc.status !== "replaced" && <ReplaceRequestButton label={doc.label} testId={`hr-document-replace-${doc.id}`} onConfirm={(reason) => requestReplacement(doc.id, reason)} />}
                    {!["replaced", "rejected"].includes(doc.status) && <RejectRequestButton label={doc.label} testId={`hr-document-reject-${doc.id}`} onConfirm={(reason) => rejectDocument(doc.id, reason)} />}
                    <button onClick={() => download(`/career-onboarding/admin/${application.id}/documents/${doc.id}/download`, doc.filename)} className={smallBtn} data-testid={`hr-document-download-${doc.id}`}><Download size={12} className="inline" /></button>
                  </span>
                </div>
                <ReviewControls id={`doc-${doc.id}`} value={reviewValue(`doc-${doc.id}`, doc.status, doc.comment)} onChange={setReview} onSave={() => saveDocument(doc.id)} busy={busy} testPrefix={`hr-document-review-${doc.id}`} />
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="mt-6 border border-white/10 p-4" data-testid="hr-esign-panel">
        <h5 className="text-white text-sm font-medium mb-4"><PenLine size={14} className="inline mr-2 text-nx-goldlight" />E-signature Requests</h5>
        <form onSubmit={sendSignatureRequest} className="grid md:grid-cols-[220px_1fr_auto] gap-3 mb-5">
          <select className={input} value={esignType} onChange={(e) => setEsignType(e.target.value)} data-testid="esign-type">
            <option value="nda">NDA</option>
            <option value="appointment_letter">Appointment Letter</option>
            <option value="employment_agreement">Employment Agreement</option>
            <option value="custom">Custom PDF Upload</option>
          </select>
          <input className={input} placeholder={esignType === "custom" ? "Custom document title" : "Optional title override"} value={esignTitle} onChange={(e) => setEsignTitle(e.target.value)} data-testid="esign-title" />
          {esignType === "custom" && <input type="file" accept=".pdf" className={input} onChange={(e) => setEsignFile(e.target.files?.[0] || null)} data-testid="esign-file" />}
          <button className={goldBtn} disabled={busy || (esignType === "custom" && !esignFile)} data-testid="esign-send">Send for E-sign</button>
        </form>
        {(data.signature_requests || []).length === 0 ? <p className="text-xs text-slate-500">No e-signature requests yet.</p> : (
          <div className="grid gap-3">
            {data.signature_requests.map((req) => (
              <div key={req.id} className="flex flex-wrap items-center gap-3 border border-white/10 p-3" data-testid={`hr-signature-${req.id}`}>
                <ShieldCheck size={15} className={req.status === "signed" ? "text-emerald-300" : "text-slate-500"} />
                <div className="min-w-52"><p className="text-sm text-white">{req.title}</p><p className="text-[11px] text-slate-500">Requested {fmt(req.requested_at)}{req.signed_at ? ` · Signed ${fmt(req.signed_at)} by ${req.typed_name}` : ""}</p></div>
                <span className={`border px-2 py-1 text-[10px] uppercase ${statusClass(req.status)}`}>{req.status}</span>
                <span className="flex gap-2 ml-auto">
                  <button onClick={() => download(`/career-onboarding/admin/${application.id}/esign/${req.id}/download`, req.filename)} className={smallBtn} data-testid={`hr-signature-download-${req.id}`}><Download size={12} className="inline" /> Original</button>
                  {req.status === "signed" && <button onClick={() => download(`/career-onboarding/admin/${application.id}/esign/${req.id}/download?certificate=true`, `signature-certificate-${req.id}.pdf`)} className={smallBtn} data-testid={`hr-signature-certificate-${req.id}`}>Certificate</button>}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-3">
        {data.status !== "approved" ? (
          <button onClick={approve} disabled={busy || data.status !== "submitted"} className={goldBtn} data-testid="approve-sync-erp"><CheckCircle2 size={13} className="inline mr-1" /> Approve & Sync ERPNext</button>
        ) : (
          <>
            <span className="text-emerald-300 text-sm" data-testid="approved-message"><CheckCircle2 size={14} className="inline mr-1" /> Approved {fmt(data.approved_at)} · ERP {data.erp_sync?.status}</span>
            <span className={`text-xs ${data.welcome_email?.sent ? "text-emerald-300" : "text-amber-300"}`} data-testid="welcome-email-status">
              {data.welcome_email?.sent ? `Welcome pack sent ${fmt(data.welcome_email.at)}` : "Welcome pack not sent"}
            </span>
            <button onClick={resendWelcome} disabled={busy} className={smallBtn} data-testid="resend-welcome-pack"><Send size={12} className="inline mr-1" /> Resend Welcome Pack</button>
          </>
        )}
        {data.status !== "submitted" && data.status !== "approved" && <span className="text-xs text-slate-500">Candidate must submit before final approval.</span>}
      </div>
    </div>
  );
};
